import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../../hooks/useCart";
import { useConfiguracionTiendaPublic } from "../../hooks/useConfiguracionTiendaPublic";
import { getDireccionesCliente } from "../../../../services/usuarios_cliente/ObtenerPefilCheckoutCliente";
import { getPaises, getRegiones, getCiudades } from "../../../../services/locacion";
import phoneCountries from "../../../../catalogs/phoneCountries";
import "./CheckoutDatos.css";
import { reservaCheckoutDatos, reservarStock,} from "../../../../services/store/Checkout";
import { validateCart } from "../../../../services/public/cartValidation";

const numOrEmpty = (value) => {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : "";
};

function CheckoutDatos() {
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, subtotal, coreChargeTotal, grandTotal, ready, replaceCart } = useCart();
  const { formatMoney } = useConfiguracionTiendaPublic();

  const token = localStorage.getItem("cliente_token");
  let cliente = null;

  try {
    cliente = JSON.parse(localStorage.getItem("cliente"));
  } catch {
    cliente = null;
  }

  const isLogged = !!token && !!cliente;
  const hasTelefono = !!cliente?.telefono && !!cliente?.codigo_telefono;
  const needsTelefono = isLogged && !hasTelefono;

  const perfilCargadoRef = useRef(false);
  const clienteCargadoRef = useRef(false);

  const [checkoutSaving, setCheckoutSaving] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Preparando tu checkout...");
  const [loadingPerfil, setLoadingPerfil] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [paises, setPaises] = useState([]);
  const [regiones, setRegiones] = useState([]);
  const [ciudades, setCiudades] = useState([]);

  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});

  // Re-validación de stock al ENTRAR al checkout (cubre la demora del login/registro).
  const [stockNotice, setStockNotice] = useState(null);
  const [stockBloqueado, setStockBloqueado] = useState(false);
  const [revisandoStock, setRevisandoStock] = useState(false);

  const [form, setForm] = useState({
    nombre: "",
    apellido: "",
    correo: "",
    telefono_codigo: phoneCountries[0]?.code || "+502",
    telefono_numero: "",

    direccion_nombre: "",
    direccion_linea_1: "",
    direccion_linea_2: "",
    referencia: "",
    pais_id: "",
    region_id: "",
    ciudad_id: "",
    ciudad: "",
    estado: "",
    codigo_postal: "",
    pais: "",
    direccion_principal: true,
  });

  useEffect(() => {
    if (!ready) return;
    if (cart.length === 0 && location.pathname !== "/store/cart") {
      navigate("/store/cart", { replace: true });
    }
  }, [ready, cart.length, location.pathname, navigate]);

  useEffect(() => {
    if (!ready) return;
    if (!isLogged) {
      navigate("/store/cart", { replace: true });
    }
  }, [ready, isLogged, navigate]);

  useEffect(() => {
    if (!isLogged || !cliente) return;
    if (clienteCargadoRef.current) return;

    clienteCargadoRef.current = true;

    setForm((prev) => ({
      ...prev,
      nombre: cliente.nombre || "",
      apellido: cliente.apellido || "",
      correo: cliente.correo || "",
      telefono_codigo: cliente.codigo_telefono || phoneCountries[0]?.code || "+502",
      telefono_numero: cliente.telefono || "",
    }));
  }, [isLogged, cliente]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await getPaises();
        if (!cancelled) {
          setPaises(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error("Error cargando países:", err);
        if (!cancelled) setPaises([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isLogged) return;
    if (perfilCargadoRef.current) return;

    perfilCargadoRef.current = true;

    const loadPerfil = async () => {
      try {
        const data = await getDireccionesCliente();
        const list = Array.isArray(data) ? data : [];

        if (list.length > 0) {
          const principal =
            list.find((dir) => Number(dir.direccion_principal) === 1) || list[0];

          const pid = numOrEmpty(principal.pais_id);
          const rid = numOrEmpty(principal.region_id);
          const cid = numOrEmpty(principal.ciudad_id);

          setForm((prev) => ({
            ...prev,
            direccion_nombre: principal.nombre_destinatario || "",
            direccion_linea_1: principal.direccion_linea_1 || "",
            direccion_linea_2: principal.direccion_linea_2 || "",
            referencia:
              principal.referencia != null ? String(principal.referencia) : "",
            pais_id: pid === "" ? "" : pid,
            region_id: rid === "" ? "" : rid,
            ciudad_id: cid === "" ? "" : cid,
            ciudad: principal.ciudad || "",
            estado: principal.region || "",
            codigo_postal:
              principal.codigo_postal != null
                ? String(principal.codigo_postal)
                : "",
            pais: principal.pais || "",
            direccion_principal: Number(principal.direccion_principal) === 1,
          }));
        }
      } catch (err) {
        console.error("Error cargando perfil:", err);
      } finally {
        setLoadingPerfil(false);
      }
    };

    loadPerfil();
  }, [isLogged]);

  useEffect(() => {
    const pid = form.pais_id;

    if (pid === "" || pid == null) {
      setRegiones([]);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const data = await getRegiones(pid);
        if (!cancelled) {
          setRegiones(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error("Error cargando regiones:", err);
        if (!cancelled) setRegiones([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [form.pais_id]);

  useEffect(() => {
    const rid = form.region_id;

    if (rid === "" || rid == null) {
      setCiudades([]);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const data = await getCiudades(rid);
        if (!cancelled) {
          setCiudades(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error("Error cargando ciudades:", err);
        if (!cancelled) setCiudades([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [form.region_id]);

  const itemCount = useMemo(() => {
    return cart.reduce((acc, item) => acc + Number(item.cantidad || 0), 0);
  }, [cart]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    setErrors((prev) => {
      if (!prev[name]) return prev;
      const copy = { ...prev };
      delete copy[name];
      return copy;
    });
  };

  const handlePaisChange = (e) => {
    const raw = e.target.value;
    const pid = raw === "" ? "" : Number(raw);
    const paisNombre =
      pid === ""
        ? ""
        : paises.find((p) => Number(p.pais_id) === pid)?.nombre || "";

    setForm((prev) => ({
      ...prev,
      pais_id: pid === "" ? "" : pid,
      region_id: "",
      ciudad_id: "",
      pais: paisNombre,
      estado: "",
      ciudad: "",
    }));

    setErrors((prev) => {
      const copy = { ...prev };
      delete copy.pais_id;
      delete copy.region_id;
      delete copy.ciudad_id;
      return copy;
    });
  };

  const handleRegionChange = (e) => {
    const raw = e.target.value;
    const rid = raw === "" ? "" : Number(raw);
    const regionNombre =
      rid === ""
        ? ""
        : regiones.find((r) => Number(r.region_id) === rid)?.nombre || "";

    setForm((prev) => ({
      ...prev,
      region_id: rid === "" ? "" : rid,
      ciudad_id: "",
      estado: regionNombre,
      ciudad: "",
    }));

    setErrors((prev) => {
      const copy = { ...prev };
      delete copy.region_id;
      delete copy.ciudad_id;
      return copy;
    });
  };

  const handleCiudadChange = (e) => {
    const raw = e.target.value;
    const cid = raw === "" ? "" : Number(raw);
    const ciudadNombre =
      cid === ""
        ? ""
        : ciudades.find((c) => Number(c.ciudad_id) === cid)?.nombre || "";

    setForm((prev) => ({
      ...prev,
      ciudad_id: cid === "" ? "" : cid,
      ciudad: ciudadNombre,
    }));

    setErrors((prev) => {
      const copy = { ...prev };
      delete copy.ciudad_id;
      return copy;
    });
  };

  const validateForm = () => {
    const nextErrors = {};

    if (needsTelefono && !String(form.telefono_numero || "").trim()) {
      nextErrors.telefono_numero = "El número de teléfono es requerido.";
    }

    if (!String(form.direccion_nombre || "").trim()) {
      nextErrors.direccion_nombre = "El destinatario es requerido.";
    }

    if (!String(form.direccion_linea_1 || "").trim()) {
      nextErrors.direccion_linea_1 = "La dirección es requerida.";
    }

    if (!form.pais_id) {
      nextErrors.pais_id = "Selecciona un país.";
    }

    if (!form.region_id) {
      nextErrors.region_id = "Selecciona una región.";
    }

    if (!form.ciudad_id) {
      nextErrors.ciudad_id = "Selecciona una ciudad.";
    }

    return nextErrors;
  };

  const buildReservaItems = () => {
    return (Array.isArray(cart) ? cart : []).map((item) => ({
      producto_id: Number(item.producto_id),
      cantidad: Number(item.cantidad || 0),
    }));
  };

  // Al entrar al checkout (ya autenticado), revalida el stock por si cambió
  // durante el login/registro. Ajusta cantidades y bloquea si algo se agotó.
  useEffect(() => {
    if (!ready) return;
    if (!Array.isArray(cart) || cart.length === 0) return;

    let alive = true;
    (async () => {
      try {
        setRevisandoStock(true);
        const result = await validateCart(cart);
        if (!alive) return;

        const byId = new Map(
          (result?.items || []).map((i) => [i.producto_id, i])
        );

        let adjusted = false;
        let agotado = false;

        const nextCart = cart.map((item) => {
          const v = byId.get(item.producto_id);
          if (!v) return item;
          if (Number(v.available) <= 0) agotado = true;
          if (v.allowed !== item.cantidad) adjusted = true;
          return { ...item, cantidad: v.allowed, stock: v.available };
        });

        if (adjusted) replaceCart(nextCart);
        setStockBloqueado(agotado);

        if (agotado) {
          setStockNotice(
            "Uno o más productos se agotaron mientras completabas tu sesión. Revisa tu carrito antes de continuar."
          );
        } else if (adjusted) {
          setStockNotice(
            "Ajustamos algunas cantidades según el stock disponible. Revisa el resumen antes de continuar."
          );
        } else {
          setStockNotice(null);
        }
      } catch (e) {
        // Validación de UX: si falla, no bloqueamos; el servidor valida al reservar.
        console.error("No se pudo revalidar stock al entrar al checkout:", e);
      } finally {
        if (alive) setRevisandoStock(false);
      }
    })();

    return () => {
      alive = false;
    };
    // Corre una sola vez al quedar listo el carrito; cart/replaceCart se leen
    // frescos y se excluyen a propósito para no re-ejecutar en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const handleContinue = async () => {
    setError("");
    const validation = validateForm();
    setErrors(validation);
  
    if (Object.keys(validation).length > 0) {
      setError("Completa los campos requeridos para continuar.");
      return;
    }
  
    try {
      setSubmitting(true);
      setCheckoutSaving(true);
      setLoadingMessage("Reservando productos de tu pedido...");
  
      const payload = {
        nombre: form.nombre,
        apellido: form.apellido,
        correo: form.correo,
        telefono_codigo: form.telefono_codigo,
        telefono_numero: form.telefono_numero,
  
        direccion_nombre: form.direccion_nombre,
        direccion_linea_1: form.direccion_linea_1,
        direccion_linea_2: form.direccion_linea_2,
        referencia: form.referencia,
        pais_id: Number(form.pais_id),
        region_id: Number(form.region_id),
        ciudad_id: Number(form.ciudad_id),
        ciudad: form.ciudad,
        estado: form.estado,
        codigo_postal: form.codigo_postal,
        pais: form.pais,
        direccion_principal: !!form.direccion_principal,
      };
  
      const itemsReserva = buildReservaItems();
  
      const reservaStockResponse = await reservarStock(itemsReserva);
      const grupoReserva = reservaStockResponse?.grupo_reserva;
  
      if (!grupoReserva) {
        throw new Error("No se pudo generar el grupo de reserva.");
      }
  
      setLoadingMessage("Guardando dirección y datos de entrega...");
  
      await reservaCheckoutDatos({
        grupo_reserva: grupoReserva,
        telefono: {
          codigo: payload.telefono_codigo || "",
          numero: payload.telefono_numero || "",
        },
        direccion: {
          direccion_nombre: payload.direccion_nombre || "",
          direccion_linea_1: payload.direccion_linea_1 || "",
          direccion_linea_2: payload.direccion_linea_2 || "",
          referencia: payload.referencia || "",
          pais_id: Number(payload.pais_id || 0),
          region_id: Number(payload.region_id || 0),
          ciudad_id: Number(payload.ciudad_id || 0),
          codigo_postal: payload.codigo_postal || "",
        },
      });
  
      sessionStorage.setItem("grupo_reserva", grupoReserva);
      sessionStorage.setItem("checkout_datos", JSON.stringify(payload));
  
      navigate("/store/checkout/metodo-envio");
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "No se pudo continuar con el checkout."
      );
      setCheckoutSaving(false);
    } finally {
      setSubmitting(false);
    }
  };

  const errClass = (field) => (errors[field] ? "is-error" : "");

  return (
    <div className="checkout-datos-page">
      <div className="checkout-datos-shell">
        <div className="checkout-datos-topbar">
          <div className="checkout-datos-brand">
            <span className="checkout-datos-brand-dot" />
            Paso 1
          </div>

          <div className="checkout-datos-steps">
            <div className="checkout-datos-step is-active">
              <span>1</span>
              <p>Datos</p>
            </div>
            <div className="checkout-datos-step-line" />
            <div className="checkout-datos-step">
              <span>2</span>
              <p>Envío</p>
            </div>
            <div className="checkout-datos-step-line" />
            <div className="checkout-datos-step">
              <span>3</span>
              <p>Pago</p>
            </div>
          </div>
        </div>

        <div className="checkout-datos-layout">
          <section className="checkout-datos-main">
            <div className="checkout-card checkout-card--hero">
              <div className="checkout-card-hero-text">
                <span className="checkout-badge">Paso 1</span>
                <h1>Datos de entrega</h1>
                <p>
                  Revisa tu información, completa la dirección y luego continuaremos
                  con el método de envío.
                </p>
              </div>

              <div className="checkout-card-hero-mini">
                <div>
                  <strong>{itemCount}</strong>
                  <span>artículos</span>
                </div>
                <div>
                  <strong>{formatMoney(grandTotal)}</strong>
                  <span>total</span>
                </div>
              </div>
            </div>

            <div className="checkout-card">
              <div className="checkout-card-header">
                <div>
                  <h2>Información del cliente</h2>
                  <p>Tus datos básicos para esta orden.</p>
                </div>
              </div>

              <div className="checkout-customer-grid">
                <div className="checkout-readonly-field">
                  <label>Nombre</label>
                  <div>
                    {form.nombre} {form.apellido}
                  </div>
                </div>

                <div className="checkout-readonly-field">
                  <label>Correo</label>
                  <div>{form.correo}</div>
                </div>

                <div className="checkout-field checkout-field--full">
                  <label>Teléfono</label>

                  {hasTelefono ? (
                    <div className="checkout-readonly-field checkout-readonly-field--phone">
                      <div>
                        {cliente?.codigo_telefono} {cliente?.telefono}
                      </div>
                    </div>
                  ) : (
                    <div className="checkout-phone-grid">
                      <select
                        name="telefono_codigo"
                        value={form.telefono_codigo}
                        onChange={handleChange}
                        className={errClass("telefono_codigo")}
                      >
                        {phoneCountries.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.flag} {c.code}
                          </option>
                        ))}
                      </select>

                      <input
                        type="text"
                        name="telefono_numero"
                        placeholder="Número de teléfono"
                        value={form.telefono_numero}
                        onChange={handleChange}
                        className={errClass("telefono_numero")}
                      />
                    </div>
                  )}

                  {errors.telefono_numero && (
                    <span className="checkout-field-error">
                      {errors.telefono_numero}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="checkout-card">
              <div className="checkout-card-header">
                <div>
                  <h2>Dirección de envío</h2>
                  <p>Usaremos esta dirección para procesar tu pedido.</p>
                </div>
              </div>

              <div className="checkout-form-grid">
                <div className="checkout-field checkout-field--full">
                  <label>Nombre del destinatario</label>
                  <input
                    type="text"
                    name="direccion_nombre"
                    placeholder="Ej. Juan Pérez"
                    value={form.direccion_nombre}
                    onChange={handleChange}
                    className={errClass("direccion_nombre")}
                  />
                  {errors.direccion_nombre && (
                    <span className="checkout-field-error">
                      {errors.direccion_nombre}
                    </span>
                  )}
                </div>

                <div className="checkout-field checkout-field--full">
                  <label>Dirección línea 1</label>
                  <input
                    type="text"
                    name="direccion_linea_1"
                    placeholder="Calle, avenida, número, colonia"
                    value={form.direccion_linea_1}
                    onChange={handleChange}
                    className={errClass("direccion_linea_1")}
                  />
                  {errors.direccion_linea_1 && (
                    <span className="checkout-field-error">
                      {errors.direccion_linea_1}
                    </span>
                  )}
                </div>

                <div className="checkout-field checkout-field--full">
                  <label>Dirección línea 2</label>
                  <input
                    type="text"
                    name="direccion_linea_2"
                    placeholder="Apartamento, suite, interior (opcional)"
                    value={form.direccion_linea_2}
                    onChange={handleChange}
                  />
                </div>

                <div className="checkout-field">
                  <label>País</label>
                  <select
                    value={form.pais_id === "" ? "" : String(form.pais_id)}
                    onChange={handlePaisChange}
                    className={errClass("pais_id")}
                  >
                    <option value="">Selecciona un país</option>
                    {paises.map((pais) => (
                      <option key={pais.pais_id} value={String(pais.pais_id)}>
                        {pais.nombre}
                      </option>
                    ))}
                  </select>
                  {errors.pais_id && (
                    <span className="checkout-field-error">{errors.pais_id}</span>
                  )}
                </div>

                <div className="checkout-field">
                  <label>Región / departamento</label>
                  <select
                    value={form.region_id === "" ? "" : String(form.region_id)}
                    onChange={handleRegionChange}
                    disabled={form.pais_id === ""}
                    className={errClass("region_id")}
                  >
                    <option value="">Selecciona una región</option>
                    {regiones.map((region) => (
                      <option key={region.region_id} value={String(region.region_id)}>
                        {region.nombre}
                      </option>
                    ))}
                  </select>
                  {errors.region_id && (
                    <span className="checkout-field-error">{errors.region_id}</span>
                  )}
                </div>

                <div className="checkout-field">
                  <label>Ciudad / municipio</label>
                  <select
                    value={form.ciudad_id === "" ? "" : String(form.ciudad_id)}
                    onChange={handleCiudadChange}
                    disabled={form.region_id === ""}
                    className={errClass("ciudad_id")}
                  >
                    <option value="">Selecciona una ciudad</option>
                    {ciudades.map((ciudad) => (
                      <option key={ciudad.ciudad_id} value={String(ciudad.ciudad_id)}>
                        {ciudad.nombre}
                      </option>
                    ))}
                  </select>
                  {errors.ciudad_id && (
                    <span className="checkout-field-error">{errors.ciudad_id}</span>
                  )}
                </div>

                <div className="checkout-field">
                  <label>Código postal</label>
                  <input
                    type="text"
                    name="codigo_postal"
                    placeholder="Código postal"
                    value={form.codigo_postal}
                    onChange={handleChange}
                    className={errClass("codigo_postal")}
                  />
                  {errors.codigo_postal && (
                    <span className="checkout-field-error">
                      {errors.codigo_postal}
                    </span>
                  )}
                </div>

                <div className="checkout-field checkout-field--full">
                  <label>Referencia</label>
                  <input
                    type="text"
                    name="referencia"
                    placeholder="Punto de referencia (opcional)"
                    value={form.referencia}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </section>

          <aside className="checkout-datos-sidebar">
            <div className="checkout-card checkout-card--sticky">
              <div className="checkout-card-header">
                <div>
                  <h2>Resumen del pedido</h2>
                  <p>Verifica tus productos antes de continuar.</p>
                </div>
              </div>

              <div className="checkout-summary-list">
                {cart.map((item) => {
                  const unit = Number(item.precio_unitario || 0);
                  const qty = Number(item.cantidad || 0);
                  const core = Number(item.core_charge || 0);

                  const lineSubtotal = unit * qty;
                  const lineCore = core * qty;

                  return (
                    <div key={item.producto_id} className="checkout-summary-item">
                      <div className="checkout-summary-item-top">
                        <div>
                          <strong>{item.nombre}</strong>
                          <span>Cantidad: {qty}</span>
                        </div>
                        <strong>{formatMoney(lineSubtotal)}</strong>
                      </div>

                      {core > 0 && (
                        <div className="checkout-summary-core">
                          <span>Core Charge</span>
                          <strong>{formatMoney(lineCore)}</strong>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="checkout-summary-totals">
                <div>
                  <span>Subtotal</span>
                  <strong>{formatMoney(subtotal)}</strong>
                </div>

                {Number(coreChargeTotal) > 0 && (
                  <div>
                    <span>Core Charge</span>
                    <strong>{formatMoney(coreChargeTotal)}</strong>
                  </div>
                )}

                <div className="checkout-summary-grand">
                  <span>Total</span>
                  <strong>{formatMoney(grandTotal)}</strong>
                </div>
              </div>

              {stockNotice && <div className="checkout-alert">{stockNotice}</div>}
              {error && <div className="checkout-alert">{error}</div>}

              <div className="checkout-actions">
                <button
                  type="button"
                  className="checkout-btn checkout-btn--ghost"
                  onClick={() => navigate("/store/cart")}
                  disabled={submitting || checkoutSaving}
                >
                  Volver al carrito
                </button>

                <button
                  type="button"
                  className="checkout-btn checkout-btn--primary"
                  onClick={handleContinue}
                  disabled={
                    submitting ||
                    loadingPerfil ||
                    checkoutSaving ||
                    revisandoStock ||
                    stockBloqueado
                  }
                >
                  {submitting
                    ? "Continuando..."
                    : revisandoStock
                    ? "Verificando stock..."
                    : "Continuar al envío"}
                </button>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {checkoutSaving && (
        <div className="checkout-datos-loading-overlay">
          <div className="checkout-datos-loading-card">
            <div className="checkout-datos-loading-track">
              <div className="checkout-datos-loading-cart">
                <span className="checkout-datos-cart-handle" />
                <span className="checkout-datos-cart-basket" />
                <span className="checkout-datos-cart-wheel checkout-datos-cart-wheel--left" />
                <span className="checkout-datos-cart-wheel checkout-datos-cart-wheel--right" />
              </div>
            </div>

            <div className="checkout-datos-loading-spinner">
              <span />
              <span />
              <span />
            </div>

            <h3>Estamos preparando tu pedido</h3>
            <p>{loadingMessage}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default CheckoutDatos;